import type { ActivityDto, ActivityState } from './types'

/**
 * What a screen-reader user is told, and when: a CHANGE of state, never a tick of a counter.
 * Counts move every two seconds while work runs; announcing them would make the page unusable
 * with a screen reader. These are the only moments worth a sentence: work began, was paused or
 * resumed, ended clean, ended with failures, was cancelled, or failed outright.
 */
export type ActivityChange =
  | { id: string; kind: 'started' | 'paused' | 'resumed' | 'finished' | 'cancelled' | 'failed'; activity: ActivityDto }
  | { id: string; kind: 'finishedWithFailures'; activity: ActivityDto }

/** The states the list was in when it was last read, by activity id. */
export type SeenStates = ReadonlyMap<string, ActivityState>

export function seenStatesOf(list: readonly ActivityDto[]): SeenStates {
  return new Map(list.map((a) => [a.id, a.state]))
}

const open = (s: ActivityState) => s === 'queued' || s === 'running'

/**
 * The changes between what was seen and what is now. An activity seen for the first time that is
 * already over is not news (it ended before this person looked); one that is going is "started".
 */
export function diffStates(prev: SeenStates, next: readonly ActivityDto[]): ActivityChange[] {
  const changes: ActivityChange[] = []
  for (const a of next) {
    const before = prev.get(a.id)
    if (before === a.state) continue
    if (before === undefined) {
      if (open(a.state)) changes.push({ id: a.id, kind: 'started', activity: a })
      continue
    }
    switch (a.state) {
      case 'paused':
        changes.push({ id: a.id, kind: 'paused', activity: a })
        break
      case 'queued':
      case 'running':
        changes.push({ id: a.id, kind: before === 'paused' ? 'resumed' : 'started', activity: a })
        break
      case 'finished':
        changes.push({ id: a.id, kind: 'finished', activity: a })
        break
      case 'finished_with_failures':
        changes.push({ id: a.id, kind: 'finishedWithFailures', activity: a })
        break
      case 'cancelled':
        changes.push({ id: a.id, kind: 'cancelled', activity: a })
        break
      case 'failed':
        changes.push({ id: a.id, kind: 'failed', activity: a })
        break
    }
  }
  return changes
}
